import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; 
import { storage } from "../firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import "./Portfolio.css";

const Portfolio = () => { 
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageCounts, setImageCounts] = useState({});
  const navigate = useNavigate(); 

  useEffect(() => {
    const myworkRef = ref(storage, "mywork/");

    listAll(myworkRef).then(async (res) => {
      const folderData = await Promise.all(
        res.prefixes.map(async (folder) => {
          const folderName = folder.name;

          // Get image list inside folder
          const images = await listAll(folder);

          if (images.items.length === 0) return null;

          // Get first image as cover
          const coverURL = await getDownloadURL(images.items[0]);

          // Store image count
          setImageCounts((prev) => ({
            ...prev,
            [folderName]: images.items.length,
          }));

          return {
            name: folderName.replace(/_/g, " ").replace(/-/g, " "),
            originalName: folderName,
            cover: coverURL,
            count: images.items.length,
            createdAt: new Date().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
          };
        })
      );

      setAlbums(folderData.filter(Boolean));
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="albums-container">
        <div className="albums-header">
          <div className="header-decoration">
            <span className="decoration-line"></span>
            <span className="decoration-dot"></span>
            <span className="decoration-line"></span>
          </div>
          <h1 className="header-title">
            <span className="title-accent">Collection</span>
          </h1>
          <p className="header-subtitle loading-text">
            Curating your photographic collection
          </p>
        </div>
        <div className="skeleton-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-cover"></div>
              <div className="skeleton-info">
                <div className="skeleton-title"></div>
                <div className="skeleton-meta"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const totalPhotos = Object.values(imageCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="albums-container">
      {/* Decorative Background */}
      <div className="background-elements">
        <div className="bg-grid"></div>
      </div>

      <div className="albums-header">
        <h1 className="header-title">
          <span className="title-accent">Collection</span>
        </h1>

        <p className="header-subtitle">
          A curated selection of {albums.length} albums containing {totalPhotos}{" "}
          moments in time
        </p>
      </div>

      {albums.length === 0 ? (
        <div className="empty-state">
          <div className="empty-illustration">
            <div className="empty-icon-wrapper">
              <svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.2"
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
            </div>
            <div className="empty-lines">
              <div className="empty-line"></div>
              <div className="empty-line"></div>
            </div>
          </div>
          <h2 className="empty-title">Begin Your Collection</h2>
          <p className="empty-description">
            Your photographic journey starts with the first album
          </p>
        </div>
      ) : (
        <div className="albums-grid">
          {albums.map((album) => (
            <div
              key={album.originalName}
              className="album-card"
              onClick={() => navigate(`/album/${album.originalName}`)} 
            >
              <div className="album-cover-container">
                <img
                  src={album.cover}
                  alt={album.name}
                  className="album-cover"
                  loading="lazy"
                />
                <div className="album-overlay">
                  <div className="album-count">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {imageCounts[album.originalName] || 0}
                  </div>
                  
                </div>
              </div>
              <div className="album-info">
                <h3 className="album-title">{album.name}</h3>
                <div className="album-meta">
                  <span className="meta-item">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {album.createdAt}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      {albums.length > 0 && (
        <div className="page-footer">
          <div className="footer-content">
            <div className="footer-divider"></div>
            <p className="footer-text">
              <span className="footer-counter">{albums.length}</span> albums •
              <span className="footer-counter"> {totalPhotos}</span> photographs
              •<span className="footer-counter"> ∞</span> memories
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
